import dayjs from "dayjs";
import type { AvailabilityTimeRange } from "@/lib/cal/availability-mapper";
import type { CalBusyTime } from "@/lib/cal/client";
import {
  EXTERNAL_APPOINTMENT_BACKGROUND_COLOR,
  EXTERNAL_APPOINTMENT_TEXT_COLOR,
  getAppointmentTypeBackgroundColor,
  getAppointmentTypeTextColor,
} from "@/lib/calendar/appointment-type-colors";
import type { IconName } from "@/lib/icons/registry";
import type { MedicalAppointment } from "@/models/encounter/MedicalAppointment";
import {
  getExternalSource,
  getExternalSourceLabel,
} from "@/models/encounter/medical-appointment-external-source";

/** Internal location type for appointment types. Mirrors PracticeAppointmentTypeLocationType. */
type LocationType = "conferencing" | "inPerson" | "phone";
export type AppointmentLocationTypeValue = LocationType;

const APPOINTMENT_LOCATION_TYPES = [
  "integrations:daily",
  "conferencing",
  "integration",
  "organizersDefaultApp",
  // Backward-compat alias for singular spelling seen in older Cal payloads.
  // Keep to avoid silently regressing legacy event-type normalization.
  "organizerDefaultApp",
  "attendeeInPerson",
  "inPerson",
  "attendeeAddress",
  "address",
  "attendeePhone",
  "somewhereElse",
  "link",
  "phone",
  "userPhone",
] as const;

type AppointmentLocationType = (typeof APPOINTMENT_LOCATION_TYPES)[number];

type CalLocationConfig =
  | {
      type: "address";
      address: string;
      public: boolean;
    }
  | {
      type: "integration";
      integration: string;
    }
  | {
      type: "attendeePhone";
    };

interface CalLocationsForTypeParams {
  locationType: LocationType;
  address?: string;
  isAddressPublic?: boolean;
  integration?: string;
}

/**
 * Maps appointment location types to FontAwesome icon names.
 */
export const APPOINTMENT_LOCATION_ICONS: Record<AppointmentLocationType, IconName<"regular">> = {
  "integrations:daily": "video",
  conferencing: "video",
  integration: "video",
  organizersDefaultApp: "video",
  organizerDefaultApp: "video",
  attendeeInPerson: "location-crosshairs",
  inPerson: "location-crosshairs",
  attendeeAddress: "location-crosshairs",
  address: "location-crosshairs",
  attendeePhone: "phone",
  somewhereElse: "location-dot",
  link: "link",
  phone: "phone",
  userPhone: "phone",
};

/**
 * Default icon when appointment location type is not recognized.
 */
export const DEFAULT_APPOINTMENT_ICON: IconName<"regular"> = "video";

export function resolveLocationTypeIcon(
  locationType: AppointmentLocationTypeValue | undefined,
): IconName<"regular"> {
  if (locationType === "conferencing") {
    return "video";
  }

  if (locationType === "phone") {
    return "phone";
  }

  if (locationType === "inPerson") {
    return "location-crosshairs";
  }

  return "location-dot";
}

/**
 * All known Cal.com location type keys (canonical casing).
 */
export const KNOWN_LOCATION_TYPES = new Set<AppointmentLocationType>(APPOINTMENT_LOCATION_TYPES);

const LEGACY_LOCATION_TYPE_ALIASES: Record<string, AppointmentLocationType> = {
  telemedicine: "conferencing",
  "in-person": "inPerson",
  "in person": "inPerson",
  inperson: "inPerson",
};

const CUSTOM_CONFERENCING_LOCATION_HINTS = [
  "video",
  "zoom",
  "meet",
  "teams",
  "webex",
  "daily",
  "virtual",
  "online",
  "telehealth",
  "telemedicine",
] as const;

const CUSTOM_PHONE_LOCATION_HINTS = ["phone", "call", "telephone"] as const;
const PHONE_WORD_REGEX = /\bphone\b/;
const PHONE_CHARACTER_REGEX = /^[+\d().\-\s]+$/;

/**
 * Location types that indicate a video conferencing appointment.
 * Includes both Cal.com webhook format and v2 API format.
 */
const CONFERENCING_LOCATIONS = new Set<AppointmentLocationType>([
  "integrations:daily",
  "conferencing",
  "integration", // Cal.com v2 API format
  "organizersDefaultApp",
  "organizerDefaultApp", // legacy singular alias
  "link", // Cal.com can return explicit telehealth links as event-type locations.
]);

/**
 * Location types that indicate a phone-based appointment.
 * Includes both Cal.com webhook format and v2 API format.
 */
const PHONE_LOCATIONS = new Set<AppointmentLocationType>(["phone", "userPhone", "attendeePhone"]);

/**
 * Location types that indicate an in-person appointment.
 * Includes both Cal.com webhook format and v2 API format.
 */
const IN_PERSON_LOCATIONS = new Set<AppointmentLocationType>([
  "attendeeInPerson",
  "inPerson",
  "attendeeAddress", // Cal.com v2 API format
  "address", // Cal.com v2 API format
]);

export function toKnownLocationType(location: unknown): AppointmentLocationType | undefined {
  if (typeof location !== "string") return undefined;
  const normalizedLocation = location.trim();

  if (APPOINTMENT_LOCATION_TYPES.includes(normalizedLocation as AppointmentLocationType)) {
    return normalizedLocation as AppointmentLocationType;
  }

  return LEGACY_LOCATION_TYPE_ALIASES[normalizedLocation.toLowerCase()];
}

/**
 * Checks if a location type indicates a video conferencing appointment.
 * Use this to determine whether to create a Daily room.
 *
 * @param location - The Cal.com location type string
 * @returns true if the location requires video conferencing
 */
export function isConferencingLocation(location: unknown): boolean {
  const normalized = toKnownLocationType(location);
  return normalized ? CONFERENCING_LOCATIONS.has(normalized) : false;
}

/**
 * Checks if a location type indicates a phone-based appointment.
 *
 * @param location - The Cal.com location type string
 * @returns true if the location is phone-based
 */
export function isPhoneLocation(location: unknown): boolean {
  const normalized = toKnownLocationType(location);
  return normalized ? PHONE_LOCATIONS.has(normalized) : false;
}

/**
 * Checks if a location type indicates an in-person appointment.
 *
 * @param location - The Cal.com location type string
 * @returns true if the location is in-person
 */
export function isInPersonLocation(location: unknown): boolean {
  const normalized = toKnownLocationType(location);
  return normalized ? IN_PERSON_LOCATIONS.has(normalized) : false;
}

/**
 * Checks if a location string is a known Cal.com location type.
 * Use this to distinguish between location type identifiers and custom addresses.
 *
 * @param location - The location string
 * @returns true if it's a known Cal.com location type
 */
export function isKnownLocationType(location: unknown): boolean {
  return toKnownLocationType(location) !== undefined;
}

function isUrlLikeLocation(location: string): boolean {
  const normalizedLocation = location.trim().toLowerCase();

  return (
    normalizedLocation.startsWith("http://") ||
    normalizedLocation.startsWith("https://") ||
    normalizedLocation.startsWith("www.")
  );
}

/**
 * Returns true when the appointment stores a custom conferencing label or URL.
 */
export function isCustomConferencingLocation(location: unknown): boolean {
  if (typeof location !== "string") {
    return false;
  }

  const normalizedLocation = location.trim().toLowerCase();
  if (!normalizedLocation || isKnownLocationType(location)) {
    return false;
  }

  if (normalizedLocation.startsWith("tel:")) {
    return false;
  }

  if (isUrlLikeLocation(normalizedLocation)) {
    return true;
  }

  return CUSTOM_CONFERENCING_LOCATION_HINTS.some((hint) => normalizedLocation.includes(hint));
}

/**
 * Returns true when the appointment stores a custom phone label or phone URI.
 */
export function isCustomPhoneLocation(location: unknown): boolean {
  if (typeof location !== "string") {
    return false;
  }

  const normalizedLocation = location.trim().toLowerCase();
  if (!normalizedLocation || isKnownLocationType(location)) {
    return false;
  }

  if (normalizedLocation.startsWith("tel:")) {
    return true;
  }

  return CUSTOM_PHONE_LOCATION_HINTS.some((hint) => normalizedLocation.includes(hint));
}

/**
 * Returns true when the appointment stores a custom physical location string.
 */
export function isCustomInPersonLocation(location: unknown): boolean {
  return (
    typeof location === "string" &&
    location.trim().length > 0 &&
    !isKnownLocationType(location) &&
    !isCustomConferencingLocation(location) &&
    !isCustomPhoneLocation(location)
  );
}

/**
 * Returns true when an explicit in-person appointment stores a concrete location.
 * The modality takes precedence over broad text hints in the location value.
 */
export function isConcreteInPersonLocation(
  location: unknown,
  locationType: AppointmentLocationTypeValue | undefined,
): location is string {
  return (
    locationType === "inPerson" &&
    typeof location === "string" &&
    location.trim().length > 0 &&
    !isKnownLocationType(location) &&
    !isManagedVideoLocation(location)
  );
}

interface ResolveAppointmentDisplayLocationTypeParams {
  appointmentLocationType?: AppointmentLocationTypeValue;
  appointmentTypeLocationType?: AppointmentLocationTypeValue;
}

/**
 * Persisted appointment location type is source of truth when present.
 * Schedule-backed location types are a temporary shim for older appointments
 * that have not yet been backfilled with the extension.
 */
export function resolveAppointmentDisplayLocationType({
  appointmentLocationType,
  appointmentTypeLocationType,
}: ResolveAppointmentDisplayLocationTypeParams): AppointmentLocationTypeValue | undefined {
  return appointmentLocationType ?? appointmentTypeLocationType;
}

interface AppointmentLocationDisplayParams {
  locationType?: AppointmentLocationTypeValue;
  callLink?: string;
  isRoomAppointment?: boolean;
  location?: string;
}

/**
 * Resolves the icon to show for an appointment using persisted modality.
 * Room appointments keep an in-person fallback while legacy rows are backfilled.
 */
export function resolveAppointmentLocationIcon({
  locationType,
  isRoomAppointment,
}: AppointmentLocationDisplayParams): IconName<"regular"> {
  if (locationType) {
    return resolveLocationTypeIcon(locationType);
  }

  if (isRoomAppointment) {
    return APPOINTMENT_LOCATION_ICONS.inPerson;
  }

  return "location-dot";
}

/**
 * Room appointments and legacy physical locations retain an in-person fallback
 * while modality rows are backfilled.
 */
export function isInPersonAppointmentDisplay({
  location,
  isRoomAppointment,
}: Omit<AppointmentLocationDisplayParams, "callLink">): boolean {
  const normalizedLocationType = toKnownLocationType(location);
  const hasPhysicalLocation =
    isInPersonLocation(location) ||
    normalizedLocationType === "somewhereElse" ||
    isCustomInPersonLocation(location);

  return Boolean(isRoomAppointment || hasPhysicalLocation);
}

//----------------------------------------------------------------------------------------------------------------------
//                                     LOCATION DISPLAY FORMATTING
//----------------------------------------------------------------------------------------------------------------------

export interface FormattedLocation {
  /** Human-readable label (e.g. "In Person", "Phone") */
  label: string;
  /** Physical address to display, if applicable */
  address?: string;
}

const LOCAL_CAL_VIDEO_DEV_PORT = "4110";
const LOCAL_CAL_HOSTS = new Set(["localhost", "127.0.0.1", "::1"]);

/**
 * Returns true for managed Daily/Cal video URLs that should not appear as physical locations.
 */
export function isManagedVideoLocation(location: string): boolean {
  try {
    const url = new URL(location);
    const hostname = url.hostname.toLowerCase().replace(/^\[(.*)\]$/, "$1");
    const pathname = url.pathname.toLowerCase();
    const isDailyHost = hostname === "daily.co" || hostname.endsWith(".daily.co");
    if (isDailyHost) {
      return true;
    }

    // Keep localhost matching narrow so unrelated local /video routes are not hidden.
    const isLocalCalHost = LOCAL_CAL_HOSTS.has(hostname) && url.port === LOCAL_CAL_VIDEO_DEV_PORT;
    const isCalHost = hostname === "cal.com" || hostname.endsWith(".cal.com") || isLocalCalHost;
    const isVideoPath = pathname === "/video" || pathname.startsWith("/video/");
    return isCalHost && isVideoPath;
  } catch {
    return false;
  }
}

interface FormatLocationForDisplayParams {
  location?: string;
}

/**
 * Normalizes Cal.com location tokens into human-friendly display labels.
 */
export function formatLocationForDisplay({
  location,
}: FormatLocationForDisplayParams): string | undefined {
  if (!location) return undefined;

  const normalizedLocation = toKnownLocationType(location);
  if (!normalizedLocation) return location;

  if (CONFERENCING_LOCATIONS.has(normalizedLocation)) return "Video";

  if (PHONE_LOCATIONS.has(normalizedLocation)) return "Phone";

  if (IN_PERSON_LOCATIONS.has(normalizedLocation) || normalizedLocation === "somewhereElse") {
    return "In Person";
  }

  if (normalizedLocation === "link") return "Link";

  return location;
}

/**
 * Builds a comma-separated practice address string from individual address components.
 * Returns undefined if no address parts are present.
 */
export function buildPracticeAddress(practice: {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}): string | undefined {
  const parts = [
    practice.addressLine1,
    practice.addressLine2,
    practice.city,
    practice.state,
    practice.zipCode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : undefined;
}

/**
 * Formats appointment location details for email using the resolved modality.
 * Known Cal tokens still map to user-facing labels for legacy appointments
 * that have not been backfilled with an explicit modality.
 */
export function formatLocationForEmail(
  location: string | undefined,
  practiceAddress?: string,
  locationType?: AppointmentLocationTypeValue,
): FormattedLocation | undefined {
  if (!location && !locationType) {
    return undefined;
  }

  if (locationType === "conferencing") {
    return undefined;
  }

  if (locationType === "phone") {
    if (!location || isManagedVideoLocation(location) || isKnownLocationType(location)) {
      return { label: "Phone" };
    }

    return { label: location };
  }

  if (locationType === "inPerson") {
    if (!location || isManagedVideoLocation(location)) {
      return { label: "In Person", address: practiceAddress };
    }

    if (location === "link") {
      return { label: "Link" };
    }

    const address = isConcreteInPersonLocation(location, locationType) ? location : practiceAddress;
    return { label: "In Person", address };
  }

  if (!location || isManagedVideoLocation(location)) {
    return undefined;
  }

  if (location === "link") {
    return { label: "Link" };
  }

  const normalizedLocationType = toKnownLocationType(location);
  if (!normalizedLocationType) {
    return { label: location };
  }

  if (CONFERENCING_LOCATIONS.has(normalizedLocationType)) {
    return undefined;
  }

  if (PHONE_LOCATIONS.has(normalizedLocationType)) {
    return { label: "Phone" };
  }

  if (
    IN_PERSON_LOCATIONS.has(normalizedLocationType) ||
    normalizedLocationType === "somewhereElse"
  ) {
    return { label: "In Person", address: practiceAddress };
  }

  return undefined;
}

/**
 * Where an email's location address came from, for delivery logs. Never log the address itself.
 * - `practice-address`: the single `Organization` address — wrong office on a multi-location
 *   practice unless the visit is at that office. Also reported when the appointment's own address
 *   equals it, since the patient sees the same text either way.
 * - `appointment-address`: the appointment's own concrete location.
 * - `none`: no address line rendered.
 */
export type EmailLocationAddressSource = "practice-address" | "appointment-address" | "none";

export function resolveEmailLocationAddressSource(
  formattedLocation: FormattedLocation | undefined,
  practiceAddress: string | undefined,
): EmailLocationAddressSource {
  if (!formattedLocation?.address) return "none";
  return formattedLocation.address === practiceAddress ? "practice-address" : "appointment-address";
}

//----------------------------------------------------------------------------------------------------------------------
//                                     LOCATION TYPE MAPPING (FHIR <-> Cal.com)
//----------------------------------------------------------------------------------------------------------------------

const DEFAULT_IN_PERSON_ADDRESS = "In-person";
const DEFAULT_VIDEO_INTEGRATION = "cal-video";

/**
 * Normalizes a Cal.com address field to a non-empty string.
 */
function normalizeCalAddress(address?: string): string {
  return address?.trim() || DEFAULT_IN_PERSON_ADDRESS;
}

/**
 * Normalizes a Cal.com integration field to a non-empty string.
 */
function normalizeCalIntegration(integration?: string): string {
  return integration?.trim() || DEFAULT_VIDEO_INTEGRATION;
}

/**
 * Maps an internal location type to Cal.com event type location config.
 * For address-based locations, Cal.com v2 requires `address` and `public`.
 */
export function calLocationsForType(
  paramsOrLocationType: LocationType | CalLocationsForTypeParams,
): CalLocationConfig[] {
  const { locationType, address, isAddressPublic, integration } =
    typeof paramsOrLocationType === "string"
      ? { locationType: paramsOrLocationType }
      : paramsOrLocationType;

  switch (locationType) {
    case "conferencing":
      return [
        {
          type: "integration",
          integration: normalizeCalIntegration(integration),
        },
      ];
    case "phone":
      return [{ type: "attendeePhone" }];
    default:
      return [
        {
          type: "address",
          address: normalizeCalAddress(address),
          public: isAddressPublic ?? true,
        },
      ];
  }
}

/**
 * Maps a recognized Cal.com location type string to an internal appointment location type.
 */
export function maybeLocationTypeFromCalLocation(calLocation: unknown): LocationType | undefined {
  const normalized = toKnownLocationType(calLocation);
  if (!normalized) return undefined;
  if (CONFERENCING_LOCATIONS.has(normalized)) return "conferencing";
  if (PHONE_LOCATIONS.has(normalized)) return "phone";
  if (IN_PERSON_LOCATIONS.has(normalized) || normalized === "somewhereElse") return "inPerson";
  return undefined;
}

/**
 * Maps a Cal.com location type string to an internal PracticeAppointmentTypeLocationType.
 * Handles both webhook format (e.g., "integrations:daily") and v2 API format (e.g., "integration").
 * Defaults to "inPerson" only for legacy callers that still require a concrete fallback.
 */
export function locationTypeFromCalLocation(calLocation: unknown): LocationType {
  const normalizedLocationType = maybeLocationTypeFromCalLocation(calLocation);
  if (normalizedLocationType) return normalizedLocationType;

  return "inPerson";
}

/**
 * Determines the internal location type from Cal.com's booking.location webhook field.
 *
 * Cal.com sends one of three things in booking.location:
 *   1. A known type string ("attendeeInPerson", "integrations:daily", "phone", etc.)
 *   2. A raw Daily.co room URL ("https://app.daily.co/...") — present even on in-person
 *      managed events because Cal.com always creates a Daily room on the platform
 *   3. An attendee-supplied address ("123 Main St, Denver CO") for "In Person (Attendee Address)" types
 *
 * Returns undefined only for Daily.co URLs, letting callers fall back to the schedule.
 */
export function resolveLocationTypeFromBookingLocation(location: string): LocationType | undefined {
  // Known Cal.com type string — most reliable signal
  if (isKnownLocationType(location)) {
    return locationTypeFromCalLocation(location);
  }

  // Raw Daily.co URL — Cal.com injects this even for in-person managed events;
  // can't distinguish conferencing from in-person from the URL alone, so defer to schedule.
  if (location.startsWith("https://") || location.startsWith("http://")) {
    return undefined;
  }

  // Plain text can be a phone number/value for phone appointment types.
  // Prefer phone when the value looks phone-like.
  const normalized = location.trim().toLowerCase();
  const digitsOnly = normalized.replace(/\D/g, "");
  const hasPhoneWord = PHONE_WORD_REGEX.test(normalized);
  const hasPhoneFormatting = normalized.startsWith("tel:") || /[()+-]/.test(normalized);
  const isBareNumericPhone = /^\d{10,15}$/.test(normalized);
  const isSpacedNumericPhone =
    PHONE_CHARACTER_REGEX.test(normalized) &&
    normalized.includes(" ") &&
    digitsOnly.length >= 10 &&
    digitsOnly.length <= 15;
  const looksLikePhone =
    normalized.startsWith("tel:") ||
    hasPhoneWord ||
    (PHONE_CHARACTER_REGEX.test(normalized) &&
      digitsOnly.length >= 7 &&
      digitsOnly.length <= 15 &&
      (hasPhoneFormatting || isBareNumericPhone || isSpacedNumericPhone));
  if (looksLikePhone) {
    return "phone";
  }

  // Everything else is non-conferencing plain text (typically in-person address).
  return "inPerson";
}

/**
 * Normalizes appointment titles by extracting content from brackets
 * and removing "between" suffix.
 */
export function normalizeAppointmentTitle(title?: string): string {
  if (!title) return "Untitled appointment";

  const match = title.match(/\s*\[(.*?)\]\s*/);
  const normalizedTitle = (match?.[1] ?? title).split("between")[0].trim();
  return normalizedTitle || "Untitled appointment";
}

/**
 * Combines the appointment type's own patient-facing description (practice-configured, e.g. "This
 * is a 30-minute video visit to discuss X") with any ad-hoc note entered at booking time
 * (patient/booker free text), so a booking email's Notes section always includes the type's
 * details even when a patient note is also present — neither should silently replace the other.
 */
export function combineAppointmentEmailNotes({
  appointmentTypeDescription,
  patientInstruction,
}: {
  appointmentTypeDescription?: string;
  patientInstruction?: string;
}): string | undefined {
  return (
    [appointmentTypeDescription?.trim(), patientInstruction?.trim()].filter(Boolean).join("\n\n") ||
    undefined
  );
}

/**
 * True when a title already states its own duration, e.g. "Follow-up (30 min)".
 * Appointment-type names commonly embed the length; detecting that lets callers
 * skip the separately-computed "(N min)" label so it isn't rendered twice.
 */
export function titleIncludesDurationLabel(title: string): boolean {
  return /\(\s*\d+\s*min\s*\)/i.test(title);
}

/**
 * Converts a 0–23 hour to its 12-hour-clock number (0→12, 13→1, …). The caller supplies am/pm.
 */
export function formatHour12(hour: number): number {
  if (hour === 0) return 12;
  if (hour > 12) return hour - 12;
  return hour;
}

/**
 * Formats a busy time slot range for display (e.g., "4-6pm").
 * Omits am/pm on start time if it matches the end time's am/pm.
 */
export function formatBusyTimeRange(start: dayjs.Dayjs, end: dayjs.Dayjs): string {
  const startHour = start.hour();
  const endHour = end.hour();
  const startMinute = start.minute();
  const endMinute = end.minute();

  const startAmPm = startHour < 12 ? "am" : "pm";
  const endAmPm = endHour < 12 ? "am" : "pm";

  const startStr =
    startMinute === 0
      ? `${formatHour12(startHour)}`
      : `${formatHour12(startHour)}:${startMinute.toString().padStart(2, "0")}`;

  const endStr =
    endMinute === 0
      ? `${formatHour12(endHour)}${endAmPm}`
      : `${formatHour12(endHour)}:${endMinute.toString().padStart(2, "0")}${endAmPm}`;

  if (startAmPm === endAmPm) {
    return `${startStr}-${endStr}`;
  }
  return `${startStr}${startAmPm}-${endStr}`;
}

/**
 * Represents a time range with start and end timestamps in minutes.
 */
export interface AppointmentTimeRange {
  startMinute: number;
  endMinute: number;
}

/**
 * Creates an array of appointment time ranges for overlap detection when filtering busy times.
 * Uses minute-precision timestamps with conservative rounding to prevent ghost segments.
 *
 * Rounding strategy:
 * - Start: floor (claim earlier boundary)
 * - End: ceil (claim later boundary)
 *
 * This ensures appointments "claim" their full time range, preventing tiny
 * leftover segments when times have second-level offsets.
 */
export function createAppointmentTimeRanges(
  appointments: MedicalAppointment[],
): AppointmentTimeRange[] {
  const ranges: AppointmentTimeRange[] = [];
  for (const appointment of appointments) {
    if (appointment.start) {
      const start = dayjs(appointment.start);
      // Use 1-hour default for appointments without an end time
      const end = appointment.end ? dayjs(appointment.end) : start.add(1, "hour");
      // Use floor for start, ceil for end to claim full time range
      const startMinute = Math.floor(start.valueOf() / 60000);
      const endMinute = Math.ceil(end.valueOf() / 60000);
      ranges.push({ startMinute, endMinute });
    }
  }
  return ranges;
}

/**
 * Represents a time segment (portion of a busy slot that doesn't overlap with appointments).
 */
export interface TimeSegment {
  startMinute: number;
  endMinute: number;
}

/**
 * Subtracts appointment time ranges from a busy time range.
 * Returns an array of non-overlapping segments that represent the portions of the busy slot
 * that don't overlap with any appointment.
 *
 * IMPORTANT: Assumes appointmentRanges is already sorted by startMinute ascending.
 * Use sortAppointmentRanges() before calling this function.
 *
 * Example:
 * - Busy slot: 3:00-5:00
 * - Appointments: 3:30-4:00, 4:00-4:30
 * - Result: [3:00-3:30, 4:30-5:00]
 */
export function subtractAppointmentsFromBusyTime(
  busyStartMinute: number,
  busyEndMinute: number,
  sortedAppointmentRanges: AppointmentTimeRange[],
): TimeSegment[] {
  const segments: TimeSegment[] = [];
  let currentStart = busyStartMinute;

  for (const range of sortedAppointmentRanges) {
    // Skip appointments that are entirely before our current position
    if (range.endMinute <= currentStart) continue;

    // Skip appointments that are entirely after the busy slot
    if (range.startMinute >= busyEndMinute) break;

    // If there's a gap before this appointment, add it as a segment
    if (range.startMinute > currentStart) {
      segments.push({
        startMinute: currentStart,
        endMinute: Math.min(range.startMinute, busyEndMinute),
      });
    }

    // Move current position past this appointment
    currentStart = Math.max(currentStart, range.endMinute);
  }

  // Add any remaining time after the last appointment
  if (currentStart < busyEndMinute) {
    segments.push({
      startMinute: currentStart,
      endMinute: busyEndMinute,
    });
  }

  return segments;
}

/**
 * Sorts appointment time ranges by start time.
 * Call once before processing multiple busy times to avoid repeated sorting.
 */
export function sortAppointmentRanges(ranges: AppointmentTimeRange[]): AppointmentTimeRange[] {
  return [...ranges].sort((a, b) => a.startMinute - b.startMinute);
}

/**
 * Splits busy times around appointments so they don't overlap.
 * Cal.com may return merged busy slots that span multiple appointments.
 * This function splits them into non-overlapping segments.
 *
 * Performance: O(A log A) for sorting + O(B * A) for splitting where B = busy times, A = appointments.
 * Sorting is done once upfront to avoid O(B * A log A) complexity.
 *
 * Rounding strategy for busy times (opposite of appointments):
 * - Start: ceil (trim to later boundary)
 * - End: floor (trim to earlier boundary)
 *
 * This ensures busy times are conservatively trimmed and never extend into
 * appointment territory when times have second-level offsets.
 *
 * @param busyTimes - Array of busy times from Cal.com
 * @param appointmentRanges - Array of appointment time ranges (will be sorted internally)
 * @returns Array of busy time objects with split segments
 */
export function splitBusyTimesAroundAppointments(
  busyTimes: CalBusyTime[],
  appointmentRanges: AppointmentTimeRange[],
): CalBusyTime[] {
  // Sort appointment ranges once upfront for O(A log A) instead of per-busy-slot
  const sortedRanges = sortAppointmentRanges(appointmentRanges);

  const result: CalBusyTime[] = [];

  for (const busyTime of busyTimes) {
    const busyStart = dayjs(busyTime.start);
    const busyEnd = dayjs(busyTime.end);
    // Use ceil for start, floor for end to conservatively trim busy times
    const busyStartMinute = Math.ceil(busyStart.valueOf() / 60000);
    const busyEndMinute = Math.floor(busyEnd.valueOf() / 60000);

    // Skip if busy time is less than 1 minute after rounding
    if (busyEndMinute <= busyStartMinute) continue;

    // Split this busy time around any overlapping appointments (uses pre-sorted ranges)
    const segments = subtractAppointmentsFromBusyTime(busyStartMinute, busyEndMinute, sortedRanges);

    // Convert each segment back to a CalBusyTime object
    for (const segment of segments) {
      result.push({
        start: dayjs(segment.startMinute * 60000).toISOString(),
        end: dayjs(segment.endMinute * 60000).toISOString(),
        source: busyTime.source,
      });
    }
  }

  return result;
}

/**
 * Parameters for calculating busy slot positioning.
 */
export interface BusySlotPositionParams {
  busyTime: CalBusyTime;
  date: dayjs.Dayjs;
  minHour: number;
  hourHeight: number;
  gapBetweenEvents: number;
}

/**
 * Result of busy slot position calculation.
 * Returns null if the slot should not be displayed.
 */
export interface BusySlotPosition {
  top: number;
  height: number;
  timeRangeLabel: string;
}

/**
 * Calculates the position and dimensions for a busy time slot.
 * Returns null if the slot should not be displayed (outside visible range, too small).
 */
export function calculateBusySlotPosition(params: BusySlotPositionParams): BusySlotPosition | null {
  const { busyTime, date, minHour, hourHeight, gapBetweenEvents } = params;

  const busyStart = dayjs(busyTime.start);
  const busyEnd = dayjs(busyTime.end);

  // Define the current day's boundaries
  const dayStart = date.startOf("day");
  const dayEnd = date.endOf("day");

  // Check if the busy slot overlaps with the current day
  const overlapsWithDay = busyStart.isBefore(dayEnd) && busyEnd.isAfter(dayStart);
  if (!overlapsWithDay) return null;

  // Clamp start and end times to the current day's boundaries
  let clampedStart = busyStart.isBefore(dayStart) ? dayStart : busyStart;
  let clampedEnd = busyEnd.isAfter(dayEnd) ? dayEnd : busyEnd;

  // Define visible calendar range (minHour to 23:59:59)
  const visibleStart = date.hour(minHour).minute(0).second(0);
  const visibleEnd = date.hour(23).minute(59).second(59);

  // Check if the busy slot overlaps with the visible range
  const overlapsWithVisible = clampedStart.isBefore(visibleEnd) && clampedEnd.isAfter(visibleStart);
  if (!overlapsWithVisible) return null;

  // Further clamp to the visible calendar range
  clampedStart = clampedStart.isBefore(visibleStart) ? visibleStart : clampedStart;
  clampedEnd = clampedEnd.isAfter(visibleEnd) ? visibleEnd : clampedEnd;

  const startHour = clampedStart.hour() + clampedStart.minute() / 60;
  const top = (startHour - minHour) * hourHeight + gapBetweenEvents;

  const durationHours = clampedEnd.diff(clampedStart, "hour", true);
  const height = Math.max(durationHours * hourHeight - gapBetweenEvents, 0);

  // Skip if height is too small to display meaningfully
  if (height < 10) return null;

  // Use original times for the time range label (not clamped)
  const timeRangeLabel = formatBusyTimeRange(busyStart, busyEnd);

  return { top, height, timeRangeLabel };
}

function parseAvailabilityRangeBoundaryToMinutes(value: string): number | null {
  const match = value.match(/^(\d{2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const hours = Number.parseInt(match[1] ?? "", 10);
  const minutes = Number.parseInt(match[2] ?? "", 10);
  const isInvalidTime =
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 24 ||
    minutes < 0 ||
    minutes > 59 ||
    (hours === 24 && minutes !== 0);
  if (isInvalidTime) {
    return null;
  }

  return hours * 60 + minutes;
}

export interface AvailabilityBackdropPositionParams {
  range: AvailabilityTimeRange;
  minHour: number;
  hourHeight: number;
}

export interface AvailabilityBackdropPosition {
  top: number;
  height: number;
}

/**
 * Calculates the position for a provider availability backdrop block.
 */
export function calculateAvailabilityBackdropPosition({
  range,
  minHour,
  hourHeight,
}: AvailabilityBackdropPositionParams): AvailabilityBackdropPosition | null {
  const startMinutes = parseAvailabilityRangeBoundaryToMinutes(range.start);
  const endMinutes = parseAvailabilityRangeBoundaryToMinutes(range.end);
  if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
    return null;
  }

  const visibleStartMinutes = minHour * 60;
  const visibleEndMinutes = 24 * 60;
  const clampedStartMinutes = Math.max(startMinutes, visibleStartMinutes);
  const clampedEndMinutes = Math.min(endMinutes, visibleEndMinutes);
  if (clampedEndMinutes <= clampedStartMinutes) {
    return null;
  }

  return {
    top: ((clampedStartMinutes - visibleStartMinutes) / 60) * hourHeight,
    height: ((clampedEndMinutes - clampedStartMinutes) / 60) * hourHeight,
  };
}

/**
 * Parameters for calculating appointment positioning.
 */
export interface AppointmentPositionParams {
  appointment: MedicalAppointment;
  minHour: number;
  hourHeight: number;
  gapBetweenEvents: number;
}

/**
 * Result of appointment position calculation.
 */
export interface AppointmentPosition {
  top: number;
  height: number;
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
}

/**
 * Calculates the position and dimensions for an appointment.
 * Returns null if the appointment has no start time.
 */
export function calculateAppointmentPosition(
  params: AppointmentPositionParams,
): AppointmentPosition | null {
  const { appointment, minHour, hourHeight, gapBetweenEvents } = params;

  if (!appointment.start) return null;

  const start = dayjs(appointment.start);
  const startHour = start.hour() + start.minute() / 60;
  const top = (startHour - minHour) * hourHeight + gapBetweenEvents;

  // Default 1 hour duration if end not specified
  const end = appointment.end ? dayjs(appointment.end) : start.add(1, "hour");
  const durationHours = end.diff(start, "hour", true);
  const height = durationHours * hourHeight - gapBetweenEvents;

  return { top, height, start, end };
}

//----------------------------------------------------------------------------------------------------------------------
//                                           APPOINTMENT CELL VIEW MODEL
//----------------------------------------------------------------------------------------------------------------------

/**
 * View model for rendering an appointment cell.
 * Contains only primitive display values - no business logic.
 */
export interface AppointmentCellViewModel {
  /** Unique identifier for React keys */
  id: string;
  /** Display name of the patient */
  patientName: string;
  /** Normalized appointment type label */
  appointmentType: string;
  /** Duration label (e.g., "30 min") or empty string */
  durationLabel: string;
  /** FontAwesome icon name for appointment location */
  icon: IconName<"regular">;
  /** Background color used for the appointment cell */
  backgroundColor: string;
  /** Foreground color with sufficient contrast */
  textColor: string;
}

/**
 * Parameters for creating an appointment cell view model.
 */
export interface ToAppointmentCellViewModelParams {
  appointment: MedicalAppointment;
  appointmentTypeLocationType?: AppointmentLocationTypeValue;
  appointmentTypeColorsById?: Record<string, string | undefined>;
  isExternalAppointmentCalendarEnabled?: boolean;
}

/**
 * Maps a MedicalAppointment to a view model for display.
 *
 * Handles:
 * - Title normalization
 * - Icon mapping based on location
 * - Patient name extraction from participants
 * - Duration calculation
 */
export function toAppointmentCellViewModel(
  params: ToAppointmentCellViewModelParams,
): AppointmentCellViewModel {
  const {
    appointment,
    appointmentTypeLocationType,
    appointmentTypeColorsById,
    isExternalAppointmentCalendarEnabled = false,
  } = params;
  const displayLocationType = resolveAppointmentDisplayLocationType({
    appointmentLocationType: appointment.locationType,
    appointmentTypeLocationType,
  });
  const conferencingCallLink =
    displayLocationType === "conferencing" ? appointment.callLink : undefined;

  // Access ID directly - throws if not set (callers must ensure ID exists)
  const id = appointment.id;

  // External titles prefix the source unless the fallback already names it.
  const externalSource = isExternalAppointmentCalendarEnabled
    ? getExternalSource(appointment.serialize())
    : undefined;
  const title = appointment.getDisplayTitle(isExternalAppointmentCalendarEnabled);
  let appointmentType: string;
  if (externalSource === undefined) {
    appointmentType = normalizeAppointmentTitle(title);
  } else {
    const sourceLabel = getExternalSourceLabel(externalSource);
    const externalTitle = title ?? "Untitled appointment";
    appointmentType =
      externalTitle === `${sourceLabel} appointment`
        ? externalTitle
        : `[${sourceLabel}] ${externalTitle}`;
  }

  // Map location to icon
  const icon = resolveAppointmentLocationIcon({
    locationType: displayLocationType,
    location: appointment.location,
    callLink: conferencingCallLink,
    isRoomAppointment: appointment.isRoomAppointment,
  });

  // Extract patient name from participants
  const patientParticipant = appointment.participants.find((p) =>
    p.actor?.reference?.startsWith("Patient/"),
  );
  // Trim before the fallback: a whitespace-only display would otherwise render
  // as a blank name in the weekly card instead of falling back to "Unknown Patient".
  const patientName = patientParticipant?.actor?.display?.trim() || "Unknown Patient";

  // Calculate duration — suppressed when the type name already embeds it (avoids "(20 min) (20 min)").
  const durationMinutes =
    appointment.start && appointment.end
      ? dayjs(appointment.end).diff(dayjs(appointment.start), "minute")
      : null;
  const durationLabel =
    durationMinutes && !titleIncludesDurationLabel(appointmentType) ? `${durationMinutes} min` : "";

  const storedAppointmentTypeColor = appointment.scheduleId
    ? appointmentTypeColorsById?.[appointment.scheduleId]
    : undefined;
  const isExternal = externalSource !== undefined;

  return {
    id,
    patientName,
    appointmentType,
    durationLabel,
    icon,
    backgroundColor: isExternal
      ? EXTERNAL_APPOINTMENT_BACKGROUND_COLOR
      : getAppointmentTypeBackgroundColor({ color: storedAppointmentTypeColor }),
    textColor: isExternal
      ? EXTERNAL_APPOINTMENT_TEXT_COLOR
      : getAppointmentTypeTextColor({ color: storedAppointmentTypeColor }),
  };
}
